/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * AppearanceTab - theme, time-format and time-zone display preferences
 */
import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  RadioGroup,
  useTheme,
} from "@lemonadetraining/web-ui";
import { doc, serverTimestamp, setDoc } from "firebase/firestore";
import {
  COLLECTIONS,
  firebaseDB,
  NOTIFICATION_TYPE,
  notifyActions,
  useAppDispatch,
  useAppSelector,
  userActions,
} from "@lemonadetraining/web-shared";
import { isTimeFormatPreference } from "@lemonadetraining/contracts";
import { formatTimeOfDay, getDeviceUses24Hour, resolveTimeFormat } from "../../lib/timeFormat";
import OptionRow from "./OptionRow";
import SavedIndicator from "./SavedIndicator";
import ShowTimesInCard from "./ShowTimesInCard";

/** Any wall-clock time renders the same shape; 17:00 is the one users reported. */
const SAMPLE_TIME = "17:00";

/**
 * Derived from the real formatter rather than written out, so the samples
 * cannot drift from what the dashboard actually renders.
 */
const buildExamples = () => ({
  device: formatTimeOfDay(
    SAMPLE_TIME,
    resolveTimeFormat("system", { deviceUses24Hour: getDeviceUses24Hour() }),
    null
  ),
  twentyFourHour: formatTimeOfDay(SAMPLE_TIME, "24h", null),
  amPm: formatTimeOfDay(SAMPLE_TIME, "12h", null),
});

const AppearanceTab: React.FC = () => {
  const dispatch = useAppDispatch();
  const uid = useAppSelector((state) => state.user.user?.uid);
  const timeFormat = useAppSelector((state) => state.user.timeFormat);
  const { theme, setTheme } = useTheme();
  const [saved, setSaved] = useState(false);
  const example = useMemo(buildExamples, []);

  const seqRef = useRef(0);

  const handleTimeFormatChange = useCallback(
    async (next: string) => {
      // Compared against what is on screen, not the last confirmed write: while
      // a write is in flight those differ, and using the latter both swallows a
      // change back to the original option and rolls back past a write that
      // already succeeded.
      if (!uid || !isTimeFormatPreference(next) || next === timeFormat) return;
      const previous = timeFormat;

      const seq = (seqRef.current += 1);
      setSaved(false);
      dispatch(userActions.setTimeFormat(next));

      try {
        // Merge, never replace: this document holds other users' settings too,
        // and a whole-document write silently reverts them to their defaults.
        await setDoc(
          doc(firebaseDB, COLLECTIONS.Users, uid, COLLECTIONS.UserSettings, "preferences"),
          { timeFormat: next, updatedAt: serverTimestamp(), updatedFrom: "web" },
          { merge: true }
        );
        if (seqRef.current !== seq) return;
        setSaved(true);
      } catch {
        if (seqRef.current !== seq) return;
        // One action restores both the store and the localStorage mirror.
        dispatch(userActions.setTimeFormat(previous));
        dispatch(
          notifyActions.sendNotification({
            severity: NOTIFICATION_TYPE.error,
            title: "",
            message: "Failed to save your time format. Please try again.",
          })
        );
      }
    },
    [uid, dispatch, timeFormat]
  );

  const hideSaved = useCallback(() => setSaved(false), []);

  return (
    <div className="flex flex-col gap-4" data-testid="appearance-tab">
      <Card>
        <CardHeader>
          <CardTitle id="appearance-theme-title">Theme</CardTitle>
          <CardDescription id="appearance-theme-note">
            Saved on this device only, so your phone and your computer can each use a different
            theme.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={theme}
            onValueChange={setTheme}
            aria-labelledby="appearance-theme-title"
            aria-describedby="appearance-theme-note"
          >
            <OptionRow id="theme-light" value="light" label="Light" />
            <OptionRow id="theme-dark" value="dark" label="Dark" />
            <OptionRow id="theme-system" value="system" label="Use device setting" />
          </RadioGroup>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <CardTitle id="appearance-time-format-title">Time format</CardTitle>
            <SavedIndicator visible={saved} onHidden={hideSaved} />
          </div>
          <CardDescription id="appearance-time-format-note">
            How times are shown across your dashboard, on class schedules and bookings.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <RadioGroup
            value={timeFormat}
            onValueChange={handleTimeFormatChange}
            aria-labelledby="appearance-time-format-title"
            aria-describedby="appearance-time-format-note"
          >
            <OptionRow
              id="time-format-system"
              value="system"
              label="Use device setting"
              describedBy="time-format-device-note"
            />
            <OptionRow
              id="time-format-24h"
              value="24h"
              label="24-hour"
              example={example.twentyFourHour}
            />
            <OptionRow id="time-format-12h" value="12h" label="AM/PM" example={example.amPm} />
          </RadioGroup>
          <p id="time-format-device-note" className="text-sm text-muted-foreground">
            Follows the clock setting on this device, so your phone and your computer can each show
            times differently. On this computer, times look like {example.device}.
          </p>
        </CardContent>
      </Card>

      <ShowTimesInCard />
    </div>
  );
};

export default AppearanceTab;
